import { useDrag, useZoom } from 'hooks/user/useAuditoriumSeats'
import { useAuditoriumReleaseSeats } from 'hooks/user/useAuditoriumSeats/useAuditoriumReleaseSeats'
import { Tooltip } from 'primereact/tooltip'
import { useEffect, useState } from 'react'
import { Ticket } from 'types/Event'
import { Seat, SeatStatus } from 'types/Location'

import {
    Cell,
    ContainerSmall,
    Grid,
    GridContainerSmall,
    GridWrapperSmall,
    TitleBox,
    WrapperColumn,
    ZoomButton,
    ZoomContainer,
} from '../auditorium.style'
import { RELEASE_COLORS, SeatColors } from './constants'
import { SeatIcon } from './seatIcon'

interface Props {
    rows: number | null
    columns: number | null
    seats: Seat[]
    tickets: Ticket[]
    middleColumn?: number
    onSelectedSeatsChange: (selectedSeats: string[]) => void
    eventId: string
    isGallery?: boolean
    sellingTypeMap?: Record<string, { isLocalSelling: boolean }>
    galleryName?: string
}

export const AuditoriumRelease = ({
    rows,
    columns,
    seats,
    tickets,
    middleColumn,
    onSelectedSeatsChange,
    eventId,
    isGallery = false,
    sellingTypeMap,
    galleryName = 'Karzat',
}: Props) => {
    const [loading, setLoading] = useState<boolean>(true)
    const { containerRef, handleDragStart, handleDragMove, handleDragEnd } = useDrag()
    const { zoomLevel, handleZoomIn, handleZoomOut } = useZoom(columns || 0)

    const { getTicketStatusOptimistic, getTicketPrice, generateSeatLabelData, handleSeatClick, selectedTickets } =
        useAuditoriumReleaseSeats(seats, onSelectedSeatsChange, tickets, eventId)

    const getSeatColor = (seat: Seat | undefined, seatStatus: SeatStatus, isSelected: boolean): string => {
        if (!seat) return SeatColors[SeatStatus.NotAvailable]
        if (seatStatus === SeatStatus.Sold && isSelected) return SeatColors[SeatStatus.Selected]
        if (seatStatus === SeatStatus.Sold) {
            if (seat.seat_id && sellingTypeMap && sellingTypeMap[seat.seat_id]?.isLocalSelling) {
                return SeatColors[SeatStatus.Sold]
            }
            return RELEASE_COLORS.soldOnline
        }
        return SeatColors[seatStatus]
    }

    useEffect(() => {
        setLoading(false)
    }, [rows, columns])

    useEffect(() => {
        if (containerRef.current && !loading) {
            const container = containerRef.current
            container.scrollLeft = container.scrollWidth / 2 - container.clientWidth / 2
        }
    }, [containerRef, loading, rows, columns])

    const isSeatClickable = (ticketStatus: SeatStatus): boolean => [SeatStatus.Sold, SeatStatus.Selected].includes(ticketStatus)

    if (rows === null || columns === null) return null

    return (
        <ContainerSmall>
            <WrapperColumn>
                <TitleBox>Színpad</TitleBox>
                <GridContainerSmall
                    ref={containerRef}
                    onMouseDown={handleDragStart}
                    onMouseMove={handleDragMove}
                    onMouseUp={handleDragEnd}
                    onMouseLeave={handleDragEnd}
                >
                    <GridWrapperSmall
                        style={{ transform: `scale(${zoomLevel})`, transformOrigin: 'center', transition: 'transform 0.3s ease' }}
                    >
                        {Array.from({ length: rows }).map((val, rowIndex) => (
                            <Grid rows={1} columns={columns} key={rowIndex}>
                                {Array.from({ length: columns }).map((value, columnIndex) => {
                                    const foundSeat = seats.find((s) => s.row === rowIndex && s.column === columnIndex)
                                    const ticketStatus = getTicketStatusOptimistic(rowIndex, columnIndex)
                                    const isSelected = foundSeat
                                        ? selectedTickets.some(
                                              (ticket: { seat_id: string | undefined }) => ticket.seat_id === foundSeat.seat_id,
                                          )
                                        : false
                                    const fillColor = getSeatColor(foundSeat, ticketStatus, isSelected)
                                    const isClickable = isSeatClickable(ticketStatus)
                                    const seatLabel = generateSeatLabelData(
                                        rowIndex,
                                        columnIndex,
                                        columns,
                                        seats,
                                        middleColumn,
                                        isGallery,
                                        galleryName,
                                    )?.label
                                    const ticketPrice = getTicketPrice(foundSeat?.seat_id)

                                    return (
                                        <Cell
                                            id={`seat-${rowIndex}-${columnIndex}-${foundSeat?.seat_id}`}
                                            key={foundSeat?.seat_id || `${rowIndex}-${columnIndex}`}
                                            onClick={isClickable ? () => handleSeatClick(rowIndex, columnIndex) : undefined}
                                            style={{
                                                cursor: isClickable ? 'pointer' : 'not-allowed',
                                            }}
                                        >
                                            {isClickable && (
                                                <Tooltip
                                                    target={`#seat-${rowIndex}-${columnIndex}-${foundSeat?.seat_id}`}
                                                    content={`${seatLabel}\n${ticketPrice} Ft`}
                                                    position="right"
                                                    showOnDisabled
                                                    showDelay={400}
                                                    hideDelay={200}
                                                />
                                            )}
                                            <div>
                                                <SeatIcon fill={fillColor} />
                                            </div>
                                        </Cell>
                                    )
                                })}
                            </Grid>
                        ))}
                    </GridWrapperSmall>
                </GridContainerSmall>
                <ZoomContainer>
                    <ZoomButton type="button" onClick={handleZoomIn} icon="pi pi-search-plus" />
                    <ZoomButton type="button" onClick={handleZoomOut} icon="pi pi-search-minus" />
                </ZoomContainer>
            </WrapperColumn>
        </ContainerSmall>
    )
}
